import { PortalSidebar, type SidebarNavSection } from "@/shared/layout/PortalSidebar";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useReviewItemsCountStore } from "@/stores/reviewItemsCountStore";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";

interface PatientSidebarProps {
  profile: PatientProfile;
}

const navSections: SidebarNavSection[] = [
  {
    title: "Overview",
    items: [
      { path: "/patient", label: "Dashboard", icon: "lucide:layout-dashboard", end: true },
      { path: "/patient/upload", label: "Upload Records", icon: "lucide:upload" },
      { path: "/patient/documents", label: "Document Panel", icon: "lucide:folder-open" },
      { path: "/patient/human-review-queue", label: "Human Review Queue", icon: "lucide:user-check" },
      { path: "/patient/health-concerns", label: "Health Concerns", icon: "lucide:alert-triangle" },
      { path: "/patient/timeline", label: "Health Timeline", icon: "lucide:clock" },
      { path: "/patient/ask", label: "Ask About Records", icon: "lucide:message-circle-question" },
      { path: "/patient/consent-privacy", label: "Consent & Privacy", icon: "lucide:shield-check" },
    ],
  },
  // Family & Caregivers section hidden from the sidebar — caregiver/dedicated-profile
  // module is temporarily hidden from end users. Routes still exist, just not linked.
  // {
  //   title: "Family & Caregivers",
  //   items: [
  //     { path: "/patient/manage-family", label: "Manage Family", icon: "lucide:users" },
  //     { path: "/patient/manage-caregivers", label: "Manage Caregivers", icon: "lucide:user-cog" },
  //     { path: "/caregiver", label: "Caregiver Dashboard", icon: "lucide:heart-handshake" },
  //   ],
  // },
  {
    title: "Health Records",
    items: [
      { path: "/patient/diagnosis", label: "Diagnoses", icon: "lucide:stethoscope" },
      { path: "/patient/complete-history", label: "Complete History", icon: "lucide:history" },
      { path: "/patient/medications", label: "Medications", icon: "lucide:pill" },
      { path: "/patient/diagnostics", label: "Diagnostics & Imaging", icon: "lucide:test-tube" },
      { path: "/patient/care-treatment", label: "Care & Treatment", icon: "lucide:clipboard-list" },
    ],
  },
  {
    title: "Insurance & Billing",
    items: [
      { path: "/patient/insurance-cards", label: "Insurance & Cards", icon: "lucide:shield" },
      { path: "/patient/claims", label: "Claims", icon: "lucide:receipt" },
    ],
  },
  {
    title: "Health Exchange",
    items: [
      { path: "/patient/fhir-export", label: "FHIR Export", icon: "lucide:file-down" },
    ],
  },
];

// Medication review items (Module 2) awaiting input show as a count on the
// Human Review Queue item (where they're reviewed); everything else is the static list above.
function withReviewBadge(sections: SidebarNavSection[], awaitingInput: number): SidebarNavSection[] {
  if (awaitingInput <= 0) return sections;
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => (item.path === "/patient/human-review-queue" ? { ...item, badge: awaitingInput } : item)),
  }));
}

export function PatientSidebar({ profile }: PatientSidebarProps) {
  const signOut = usePatientAuthStore((s) => s.signOut);
  const awaitingInput = useReviewItemsCountStore((s) => s.awaitingInput);

  return (
    <PortalSidebar
      logoTitle="MedSyncIQ"
      logoSubtitle="Healthcare Platform"
      profileSlot={<ProfileSwitcher profile={profile} />}
      navSections={withReviewBadge(navSections, awaitingInput)}
      onSignOut={() => signOut()}
    />
  );
}
